export enum ContentType {
	whiteboard = 'whiteboard',
	liveDoc = 'liveDoc',
	database = 'database',
	workItem = 'workItem',
	slides = 'slides',
	dashboard = 'dashboard',
	page = 'page',
}

/**
 * Normalises the `type`/`subType` returned for finalized Confluence content into
 * a {@link ContentType}. Shared so the preview card, title dropdown and finalize
 * dialog resolve content types identically.
 */
export const getContentTypeFromPublishedContent = (
	type: string,
	subType: string | null,
): ContentType | null => {
	// CNS-26518: slides may be returned by AGG as a top-level `'slide'` content
	// type (modern data shape used after publishing slides to Confluence) or as
	// a `'whiteboard'` with `subType === 'slide'` (legacy shape). Normalise both
	// here so the slides icon never disappears from the chat preview card.
	if (type === 'slide') {
		return ContentType.slides;
	}
	if (type === 'whiteboard') {
		return subType === 'slide' ? ContentType.slides : ContentType.whiteboard;
	}
	if (type === 'page') {
		return subType === 'live' ? ContentType.liveDoc : ContentType.page;
	}
	if (type === 'database') {
		return ContentType.database;
	}
	return null;
};

/**
 * The content type to show for a preview item. Once content is published its real type/subType wins:
 * the draft's expected type only describes what an action was going to create. Shared so the preview
 * card, title dropdown and finalize dialog all switch from expected to real type at the same point.
 *
 * Takes an already-resolved `draftContentType` rather than an actionKey: resolving an actionKey to a
 * ContentType lives in `conversation-assistant-action-helpers/utils/get-content-type-from-action-key`,
 * which depends on this store package, so this function can't call back into it without a cycle.
 * Callers resolve the draft type first, then pass it in here.
 */
export const resolveDisplayContentType = ({
	publishedContent,
	draftContentType,
}: {
	publishedContent?: { type: string; subType: string | null };
	draftContentType: ContentType | null;
}): ContentType | null =>
	publishedContent
		? getContentTypeFromPublishedContent(publishedContent.type, publishedContent.subType)
		: draftContentType;
